import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Chip,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import PollIcon from '@mui/icons-material/Poll';
import PublicIcon from '@mui/icons-material/Public';
import { getOpinionStats, OpinionStatsResponse } from '../../services/OpinionService';

const OpinionStats: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<OpinionStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      if (!id) return;
      try {
        const stats = await getOpinionStats(id);
        setData(stats);
      } catch (err: any) {
        console.error('Error loading opinion stats:', err);
        const message =
          err?.response?.data?.error ||
          (err?.response?.status === 403
            ? 'You can only view stats for your own opinions.'
            : 'Failed to load stats');
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data || error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Stats not available'}
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/home')}
          >
            Go back
          </Button>
        </Container>
      </Box>
    );
  }

  const { opinion, stats } = data;

  const title =
    opinion.content.length > 80
      ? opinion.content.substring(0, 80) + '...'
      : opinion.content;

  return (
    <>
      <Helmet>
        <title>Opinion Stats | OpinLoop</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(`/opinions/${opinion._id}`)}
              >
                Back to opinion
              </Button>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  Insights & Stats
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Only visible to you as the owner of this opinion.
                </Typography>
              </Box>
            </Box>
            {opinion.isPublic && (
              <Chip
                icon={<PublicIcon />}
                label="Public opinion"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Overview card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Opinion
              </Typography>
              <Typography variant="h6" gutterBottom>
                {title}
              </Typography>
              {opinion.category && (
                <Chip
                  label={opinion.category}
                  size="small"
                  sx={{ mt: 0.5, mr: 1 }}
                  color="primary"
                  variant="outlined"
                />
              )}
              {opinion.targetAudience && opinion.targetAudience.length > 0 && (
                <Chip
                  label={`${opinion.targetAudience.length} audience tags`}
                  size="small"
                  sx={{ mt: 0.5 }}
                  variant="outlined"
                />
              )}
            </CardContent>
          </Card>

          {/* Stats grid */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Engagement
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <ThumbUpIcon color="success" />
                      <Box flex={1}>
                        <Typography variant="body2">Agree</Typography>
                        <Typography variant="h5" fontWeight={700}>
                          {stats.agreeCount}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <ThumbDownIcon color="error" />
                      <Box flex={1}>
                        <Typography variant="body2">Disagree</Typography>
                        <Typography variant="h5" fontWeight={700}>
                          {stats.disagreeCount}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CommentIcon color="primary" />
                      <Box flex={1}>
                        <Typography variant="body2">Comments</Typography>
                        <Typography variant="h5" fontWeight={700}>
                          {stats.commentCount}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <PollIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Peer question results
                    </Typography>
                  </Box>

                  {stats.questionStats.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No interactive questions were added for this opinion.
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {stats.questionStats.map((q, idx) => (
                        <Box key={idx}>
                          <Typography variant="body2" fontWeight={600} gutterBottom>
                            Q{idx + 1}. {q.questionText}
                          </Typography>
                          {q.options.map((opt, optIdx) => (
                            <Box key={optIdx} mb={0.75}>
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">{opt.text}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {opt.votes} ({opt.percentage}%)
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={opt.percentage}
                                sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                              />
                            </Box>
                          ))}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            Total responses: {q.totalVotes}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default OpinionStats;

